import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import axiosInstance, { clearTokens } from '@/lib/axios/axiosInstance'
import { ROUTES } from '@/constants'
import { removeDataFromLocalStorage, saveToLocalStorage } from '@/utils/localStorage'
import cookieService from '@/utils/cookies'
import { apiPath } from '@/constants/api-paths'
import { isPatientPortal } from '@/utils/portalDetection'
import { useAppDispatch, showSnackbar } from '@/store'
import type { ClinicianLoginPayload, PatientLoginPayload, UserRole } from './auth.types'
import type { ResponseContentEntity } from '@/models/response-types'

interface LoginTokenData {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

interface JwtPayload {
  exp: number
  iat: number
  sub: string
  email?: string
  email_verified?: boolean
  preferred_username?: string
  realm_access?: { roles: string[] }
  scope?: string
}

/*** Roles that map to admin portal */
const ADMIN_ROLES = new Set(['SUPER_ADMIN', 'TENANT_ADMIN', 'TENANT_SUPPORT'])

/*** Roles that map to clinician/provider portal */
const CLINICIAN_ROLES = new Set([
  'PHYSICIAN',
  'NURSE',
  'THERAPIST',
  'FRONTDESK',
  'BILLER',
  'SUPERVISING_PHYSICIAN',
  'MEDICAL_ASSISTANT',
  'PROVIDER_GROUP_ADMIN',
  'PSYCHIATRIST',
])

/***Patient role(s) */
const PATIENT_ROLES = new Set(['PATIENT'])

/*** Keycloak default roles to ignore */
const IGNORE_ROLES = new Set([
  'offline_access',
  'uma_authorization',
  'default-roles-qa',
  'default-roles-development',
  'default-roles-prod',
  'default-roles-develop',
])

type LoginTokenResult =
  | { ok: true; role: UserRole; redirectTo: string }
  | { ok: false; error: string }

/**
 * Derives the user role and redirect route from JWT roles.
 * Also enforces portal restriction — a user whose roles don't match the
 * portal they're signing in from is rejected.
 */
function processLoginToken(accessToken: string, isPatient: boolean): LoginTokenResult {
  const decoded: JwtPayload = jwtDecode(accessToken)
  const jwtRoles = decoded.realm_access?.roles?.filter((r) => !IGNORE_ROLES.has(r)) || []
  const hasAdminRole = jwtRoles.some((r) => ADMIN_ROLES.has(r))
  const hasClinicianRole = jwtRoles.some((r) => CLINICIAN_ROLES.has(r))
  const hasPatientRole = jwtRoles.some((r) => PATIENT_ROLES.has(r))

  if (isPatient) {
    // Patient portal: only PATIENT role allowed.
    if (!hasPatientRole || hasAdminRole || hasClinicianRole) {
      return {
        ok: false,
        error: 'This account is not a patient account. Please use the clinician portal.',
      }
    }
    return { ok: true, role: 'patient', redirectTo: ROUTES.PATIENT.DASHBOARD }
  }
  // Clinician portal: patient-only accounts are rejected.
  if (hasPatientRole && !hasAdminRole && !hasClinicianRole) {
    return {
      ok: false,
      error: 'This account is a patient account. Please use the patient portal.',
    }
  }
  if (hasAdminRole) {
    return { ok: true, role: 'admin', redirectTo: ROUTES.APP.ROOT }
  }
  if (hasClinicianRole) {
    return { ok: true, role: 'clinician', redirectTo: ROUTES.APP.ROOT }
  }
  // Fallback
  return { ok: true, role: 'clinician', redirectTo: ROUTES.APP.ROOT }
}

/**
 * Clears auth tokens from local storage. Used when we reject a login
 * attempt for role/portal mismatch after the token has been issued.
 */
const clearAuthStorage = () => {
  cookieService.removeValue('access_token')
  cookieService.removeValue('refresh_token')
  cookieService.removeValue('expires_in')
  cookieService.removeValue('token_type')
  removeDataFromLocalStorage('role')
}

/**
 * Resolves where to land after a successful login.
 *
 * ProtectedRoute stores the originally-requested route as `location.state.from`
 * when it bounces an unauthenticated user to login (e.g. a patient opening a
 * shared /telehealth/{room} call link). We honor that destination — but only
 * when it's a same-origin in-app path and not itself a login/auth route, to
 * avoid open-redirect loops. Otherwise fall back to the role's dashboard.
 */
const resolveRedirect = (location: Location, fallback: string): string => {
  const from = (location.state as { from?: Location } | null)?.from
  const target = from?.pathname
  if (!target || !target.startsWith('/') || target.startsWith('/auth')) return fallback
  return `${target}${from?.search ?? ''}${from?.hash ?? ''}`
}

/**
 * useLogin — Clinician & Admin login.
 * Rejects patient-only accounts with a snackbar error.
 */
export const useLogin = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: async (payload: ClinicianLoginPayload) => {
      const response = (await axiosInstance.post('/api/master/login', {
        username: payload.username,
        password: payload.password,
        isPatient: false,
      })) as unknown as ResponseContentEntity<LoginTokenData>
      return response.data
    },
    onSuccess: (tokenData) => {
      const result = processLoginToken(tokenData.access_token, false)
      if (!result.ok) {
        clearAuthStorage()
        dispatch(showSnackbar({ severity: 'error', message: result.error }))
        return
      }
      saveToLocalStorage('role', result.role)
      navigate(resolveRedirect(location, result.redirectTo), { replace: true })
    },
  })
}

/**
 * usePatientLogin — Patient login.
 * Rejects clinician/admin accounts with a snackbar error.
 */
export const usePatientLogin = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: async (payload: PatientLoginPayload) => {
      const response = (await axiosInstance.post('/api/master/login', {
        username: payload.username,
        password: payload.password,
        isPatient: true,
      })) as unknown as ResponseContentEntity<LoginTokenData>
      return response.data
    },
    onSuccess: (tokenData) => {
      const result = processLoginToken(tokenData.access_token, true)
      if (!result.ok) {
        clearAuthStorage()
        dispatch(showSnackbar({ severity: 'error', message: result.error }))
        return
      }
      saveToLocalStorage('role', result.role)
      navigate(resolveRedirect(location, result.redirectTo), { replace: true })
    },
  })
}

/**
 * usePortalLogin — single hook that auto-detects which login to use.
 * Reads portal from URL/domain via detectPortal().
 */
export const usePortalLogin = () => {
  const patientPortal = isPatientPortal()
  const clinicianLogin = useLogin()
  const patientLogin = usePatientLogin()

  return patientPortal ? patientLogin : clinicianLogin
}

/**
 * useAccessToken — exchanges a refresh token for a new access token.
 * POST /api/master/access-token  body: { refreshToken: string }
 */
export const useAccessToken = () => {
  return useMutation({
    mutationFn: async (refreshToken: string) => {
      const response = await axiosInstance.post(apiPath?.REFRESH_TOKEN, { refreshToken })
      return (response as unknown as { data: LoginTokenData }).data
    },
  })
}

/**
 * useForgotPassword — sends a password reset OTP to the given email.
 */
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (payload: { email: string; isPatient: boolean }) => {
      const response = await axiosInstance.post(apiPath.FORGOT_PASSWORD, payload)
      return (response as unknown as { data: unknown }).data
    },
  })
}

/**
 * useForgotPasswordInvite — sends a password reset OTP to the given email.
 */
export const useForgotPasswordInvite = () => {
  return useMutation({
    mutationFn: async (payload: { email: string; isPatient: boolean }) => {
      const response = await axiosInstance.post(apiPath.FORGOT_PASSWORD_INVITE, payload)
      return (response as unknown as { data: unknown }).data
    },
  })
}

/**
 * useVerifyOtp — verifies the OTP code for a given username.
 */
export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: async (payload: { username: string; otp: string; isInvitation: boolean }) => {
      const { isInvitation, ...body } = payload
      const response = await axiosInstance.post(
        `${apiPath.VERIFY_OTP}?isInvitation=${isInvitation}`,
        body,
      )
      return (response as unknown as { data: unknown }).data
    },
  })
}

/**
 * useSetPassword — sets a new password after OTP verification.
 */
export const useSetPassword = () => {
  return useMutation({
    mutationFn: async (payload: { newPassword: string; emailId: string; isPatient: boolean }) => {
      const response = await axiosInstance.post(apiPath.SET_RESET_PASSWORD, payload)
      return (response as unknown as { data: unknown }).data
    },
  })
}

/**
 * useLogout — calls logout API, clears tokens, redirects to login.
 */
export const useLogout = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const logout = async (redirectTo?: string) => {
    try {
      await axiosInstance.post(apiPath.LOGOUT_USER)
    } catch {
      // Logout API failed, still clear locally
    }
    clearTokens()
    // Nuke the React Query cache so the next user's session doesn't see
    // the previous user's profile / patient list / etc. on first paint.
    // Without this, queries with non-zero staleTime would serve cached
    // data for the old user before the refetch completes.
    queryClient.clear()
    navigate(redirectTo ?? ROUTES.AUTH.CLINICIAN_LOGIN, { replace: true })
  }

  return { logout }
}
